// World map of the expeditions: an old-atlas silhouette with one pin per artwork.
// Coordinates are equirectangular: x = lon + 180, y = 90 - lat, in a 360×180 box.

const CONTINENTS = [
  "12,24 40,20 85,15 110,28 125,40 105,48 100,60 83,64 75,70 90,75 97,82 80,72 62,58 55,42 30,30",
  "100,80 120,80 130,90 145,98 140,112 125,125 112,145 105,135 108,110 100,95",
  "170,54 170,46 180,42 185,32 200,20 240,18 280,14 320,18 360,22 360,28 340,30 320,38 302,50 302,60 288,70 280,80 285,88 275,84 260,82 252,70 240,65 232,76 224,78 215,60 208,53 202,53 195,50 180,54",
  "163,75 170,58 190,53 212,59 223,78 231,79 220,95 215,115 200,125 192,108 189,86 172,85",
  "294,112 310,102 322,101 333,115 327,128 310,122"
];

export const project = ([lon, lat]) => ({ x: lon + 180, y: 90 - lat });

// Pins that share (almost) the same place fan out in a small ring so all of
// them stay tappable: three Paris artworks, for example.
export function spreadPins(levels) {
  const groups = new Map();
  levels.forEach((level, index) => {
    const { x, y } = project(level.map);
    const key = `${Math.round(x / 4)}:${Math.round(y / 4)}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push({ index, x, y });
  });
  const pins = new Array(levels.length);
  for (const group of groups.values()) {
    if (group.length === 1) { pins[group[0].index] = { x: group[0].x, y: group[0].y }; continue; }
    const cx = group.reduce((sum, pin) => sum + pin.x, 0) / group.length;
    const cy = group.reduce((sum, pin) => sum + pin.y, 0) / group.length;
    group.forEach((pin, position) => {
      const angle = -Math.PI / 2 + position * (2 * Math.PI / group.length);
      pins[pin.index] = { x: cx + Math.cos(angle) * 5.5, y: cy + Math.sin(angle) * 5.5 };
    });
  }
  return pins;
}

const curve = (a, b) => {
  const mx = (a.x + b.x) / 2;
  const my = (a.y + b.y) / 2 - Math.min(26, Math.abs(b.x - a.x) * .22 + 4);
  return `M${a.x} ${a.y} Q${mx} ${my} ${b.x} ${b.y}`;
};

const escape = value => String(value).replace(/[&<>"]/g, char => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[char]));

/**
 * @param {object} options
 * @param {Array} options.levels localized levels
 * @param {Array} options.expeditions localized expeditions
 * @param {number} options.completed number of completed levels
 * @param {string} options.activeExpedition expedition id to highlight
 * @param {object} options.labels { locked, done, current }
 */
export function renderMapSvg({ levels, expeditions, completed, activeExpedition, labels }) {
  const pins = spreadPins(levels);
  const routes = expeditions.map(expedition => {
    const stops = levels.map((level, index) => ({ level, index })).filter(item => item.level.expedition === expedition.id);
    const active = expedition.id === activeExpedition;
    const done = stops.every(item => item.index < completed);
    const path = stops.slice(1).map((item, position) => curve(pins[stops[position].index], pins[item.index])).join(" ");
    return `<path class="atlas-route${active ? " active" : ""}${done ? " done" : ""}" d="${path}" data-expedition="${expedition.id}"></path>`;
  }).join("");
  const markers = levels.map((level, index) => {
    const pin = pins[index];
    const state = index < completed ? "done" : index === completed ? "current" : "locked";
    const active = level.expedition === activeExpedition;
    const status = state === "done" ? labels.done : state === "current" ? labels.current : labels.locked;
    return `<g class="atlas-pin atlas-pin--${state}${active ? " active" : ""}" data-level="${index}" tabindex="0" role="button" aria-label="${escape(level.city)}: ${escape(status)}" transform="translate(${pin.x.toFixed(2)} ${pin.y.toFixed(2)})">
      <circle class="atlas-pin__hit" r="9"></circle>
      ${state === "current" ? '<circle class="atlas-pin__glow" r="7"></circle>' : ""}
      <circle class="atlas-pin__dot" r="${state === "current" ? 3.2 : 2.5}"></circle>
      ${state === "done" ? '<path class="atlas-pin__check" d="M-1.4 0.2 L-0.4 1.2 L1.5 -1"></path>' : ""}
      ${state === "current" ? `<text class="atlas-pin__label" y="-6.5" text-anchor="middle">${escape(level.city)}</text>` : ""}
    </g>`;
  }).join("");
  return `<svg class="atlas-map__svg" viewBox="0 0 360 150" preserveAspectRatio="xMidYMid meet" aria-hidden="false">
    <defs>
      <pattern id="atlas-dots" width="3" height="3" patternUnits="userSpaceOnUse"><circle cx=".9" cy=".9" r=".42" class="atlas-dot"></circle></pattern>
      <radialGradient id="atlas-glow"><stop offset="0" stop-color="#f2c57d" stop-opacity=".85"></stop><stop offset="1" stop-color="#f2c57d" stop-opacity="0"></stop></radialGradient>
    </defs>
    <g transform="translate(0 -14)">
      <g class="atlas-land">${CONTINENTS.map(points => `<polygon points="${points}"></polygon>`).join("")}</g>
      ${routes}
      ${markers}
    </g>
  </svg>`;
}
